import type { ReactElement } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CampaignUnavailable } from './CampaignUnavailable';

/**
 * Shared not-found guard for the campaign-detail adapters in the brand and admin
 * apps. Returns a <CampaignUnavailable> element when the slug resolver reported a
 * 404 — the campaign doesn't exist OR the signed-in user has no access (the BE
 * merges both into one privacy-preserving 404) — and null otherwise.
 *
 * Call it after every other hook, then early-return its result so a 404 shows an
 * explicit message instead of the empty campaign shell:
 *
 *     const notFoundView = useCampaignNotFoundGuard(hookValue.notFound);
 *     if (notFoundView) return notFoundView;
 *
 * Both apps run TanStack Router and route to `/campaigns`, so the navigation
 * lives here once instead of being copy-pasted into each app's adapter.
 */
export function useCampaignNotFoundGuard(notFound: boolean, audience: 'brand' | 'admin' = 'brand'): ReactElement | null {
  const navigate = useNavigate();
  if (!notFound) return null;
  return (
    <CampaignUnavailable audience={audience} onGoToCampaigns={() => navigate({ to: '/campaigns' })} />
  );
}
